/**
 * Copyright (c) 2022 - Present. Blend Labs, Inc. All rights reserved
 * Blend Confidential - Restricted
 */
import * as React from 'react';
import { useState, useMemo, useContext, useRef } from 'react';
import { get, debounce } from 'lodash';

import { getRedirectTaskFetchUrl } from 'blb-api/tasks';
import { MeContext } from 'blb-contexts/Me';
import { BlendBridge } from 'blb-types/blendBridge';
import { errorMessage } from 'blb-utils/errors';
import { openUrlInNewTab, openUrlInSameTab } from 'blb-utils/misc';

import { Task } from './Task';
import { IframeTaskModal } from './IframeTaskModal';

/**
 * Debounce wait is set to 5000 ms or 5 seconds, so that we prevent frontend from calling
 * fetchRedirectUrl endpoint multiple times, once its clicked. If there is an error during
 * fetching the action would be unlocked so the user will be able to retry again.
 */
const DEBOUNCE_MS = 5000;

export namespace BaseRedirectTask {
  export interface Props extends BlendBridge.API.BaseRedirectTask {
    id?: string;
    iframeComponents?: {
      children?: JSX.Element;
      subtitle?: JSX.Element;
    };
    onFetchAndDecide?: (response: BlendBridge.API.RedirectTaskFetchUrlResponse, isTagAllDocs?: boolean) => void;
    onModalClose?: () => void;
    onSuccess: () => Promise<any>;
    taskId: string;
  }
}

export const BaseRedirectTask: React.FC<BaseRedirectTask.Props> = ({
  taskHeader,
  disabledReason,
  id,
  isDisabled,
  iframeComponents,
  onFetchAndDecide,
  onModalClose,
  onSuccess,
  redirectUrl,
  taskId,
  type,
  buttonLabel = 'Open',
  urlOpenMethod,
}) => {
  const me = useContext(MeContext);
  const [error, setError] = useState<string | undefined>(undefined);
  const [isLoading, setLoading] = useState<boolean>(false);
  const [showIframeModal, setShowIframeModal] = useState<boolean>(false);
  const [iframeModalUrl, setIframeModalUrl] = useState<string>('');
  const isTagAllDocsRef = useRef(false);
  id = id ?? `it-task-${taskId}`;

  const openUrlInIframeModal = (url: string): void => {
    setIframeModalUrl(url);
    setShowIframeModal(true);
    setLoading(false);
  };

  const directOpenUrlFunctionMap: Record<BlendBridge.API.DirectUrlOpenMethod, (url: string) => void> = {
    iframeModal: openUrlInIframeModal,
    newTab: openUrlInNewTab,
    redirect: openUrlInSameTab,
  };

  const fetchUrlAndDecideAction = async () => {
    try {
      setLoading(true);
      const { data: response } = await getRedirectTaskFetchUrl(taskId, isTagAllDocsRef.current);
      onFetchAndDecide && onFetchAndDecide(response, isTagAllDocsRef.current);
      const { method, url } = response;
      directOpenUrlFunctionMap[method](url);
    } catch (err) {
      setError(get(err, 'response.data.error.message', errorMessage('REDIRECTING_ERROR', me?.principalType)));
      setLoading(false);
      isTagAllDocsRef.current = false;
    }
  };

  const openUrlFunctionMap: Record<BlendBridge.API.UrlOpenMethod, (() => Promise<void>) | ((url: string) => void)> = {
    ...directOpenUrlFunctionMap,
    fetchAndDecide: fetchUrlAndDecideAction,
  };

  const openUrl = async () => {
    if (redirectUrl !== undefined) {
      return openUrlFunctionMap[urlOpenMethod](redirectUrl);
    }
    if (urlOpenMethod === BlendBridge.API.FetchUrlOpenMethod.FETCH_AND_DECIDE) {
      return fetchUrlAndDecideAction();
    }
    setError(errorMessage('REDIRECTING_ERROR', me?.principalType));
    isDisabled = true;
  };

  const renderIframeModal = () =>
    showIframeModal && (
      <IframeTaskModal
        handleClose={() => {
          setShowIframeModal(false);
          isTagAllDocsRef.current = false;
          onModalClose && onModalClose();
        }}
        id={id}
        onSuccess={async () => {
          await onSuccess();
          setLoading(false);
          isTagAllDocsRef.current = false;
          onModalClose && onModalClose();
        }}
        subtitleComponents={iframeComponents?.subtitle}
        taskId={taskId}
        title={taskHeader}
        url={iframeModalUrl}
      >
        {iframeComponents?.children}
      </IframeTaskModal>
    );

  const debouncedMemoizedOpenUrlFunc = useMemo(
    () => debounce(openUrl, DEBOUNCE_MS, { leading: true, trailing: false }),
    [redirectUrl, taskId],
  );

  const hasTagAllDocsButton =
    type === BlendBridge.TaskTypes.TAGGING && urlOpenMethod === BlendBridge.API.FetchUrlOpenMethod.FETCH_AND_DECIDE;
  const extraActions = hasTagAllDocsButton
    ? [
        {
          label: 'Tag all documents',
          onClick: () => {
            isTagAllDocsRef.current = true;
            void debouncedMemoizedOpenUrlFunc();
          },
        },
      ]
    : [];

  return (
    <Task
      buttonLabel={buttonLabel}
      disabledReason={disabledReason}
      error={error}
      extraActions={extraActions}
      isButtonLoading={isLoading}
      isDisabled={isDisabled}
      onClick={debouncedMemoizedOpenUrlFunc}
      taskHeader={taskHeader}
      taskId={taskId}
      type={type}
    >
      {renderIframeModal()}
    </Task>
  );
};
